import React, { useState, useEffect } from 'react';
import './Tutorial.css';

const Tutorial = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const tutorialSteps = [
    {
      piece: { type: 'king', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'King',
      description: 'The King is the most important piece in Ranga. Similar to International Chess, it moves one square in any direction. The king is put into check when it is under attack by an enemy piece and checkmate if it is under attack by an enemy piece but can\'t move anywhere. The King cannot move into check. "Humbling" (like castling in International Chess) allows the King to move 2 squares and the Donkey to move 3 squares if not under check and neither the donkey or King have been moved.',
      validMoves: [
        { row: 5, col: 4 }, { row: 5, col: 5 }, { row: 4, col: 5 },
        { row: 3, col: 5 }, { row: 3, col: 4 }, { row: 3, col: 3 },
        { row: 4, col: 3 }, { row: 5, col: 3 }
      ],
      opponentPieces: []
    },
    {
      piece: { type: 'pawn', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Pawn',
      description: 'Pawns are another familiar piece from International Chess. They move forward one square. They capture diagonally forward. When a pawn reaches the opposite end, it can promote to any piece except King or Spy. Unlike International Chess, there is no double move on the first turn.',
      validMoves: [
        { row: 5, col: 4 }, // Forward
        { row: 5, col: 3 }, // Diagonal capture (shown with opponent)
        { row: 5, col: 5 }  // Diagonal capture (shown with opponent)
      ],
      opponentPieces: [
        { type: 'pawn', color: 'black', row: 5, col: 3 },
        { type: 'pawn', color: 'black', row: 5, col: 5 }
      ]
    },
    {
      piece: { type: 'knight', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Knight',
      description: 'Knights move in an L-shape: 2 squares in one direction and 1 square perpendicular - just like they do in International Chess. Knights can jump over other pieces.',
      validMoves: [
        { row: 6, col: 3 }, { row: 6, col: 5 },
        { row: 2, col: 3 }, { row: 2, col: 5 },
        { row: 3, col: 2 }, { row: 5, col: 2 },
        { row: 3, col: 6 }, { row: 5, col: 6 }
      ],
      opponentPieces: []
    },
    {
      piece: { type: 'cannon', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Cannon',
      description: 'Cannons are familiar to Xianqi players. They move like Rooks from International Chess (horizontally or vertically any distance). To capture, they must jump over exactly one piece. They can move to empty squares normally without jumping.',
      validMoves: [
        // Normal rook-like moves (empty squares)
        { row: 4, col: 3 }, { row: 4, col: 5 }, { row: 3, col: 4 }, { row: 5, col: 4 },
        // Jump captures (must have screen piece between)
        { row: 4, col: 1 }, // Can capture by jumping over piece at col 2
        { row: 4, col: 7 }, // Can capture by jumping over piece at col 6
        { row: 1, col: 4 }, // Can capture by jumping over piece at row 2
        { row: 7, col: 4 }  // Can capture by jumping over piece at row 6
      ],
      opponentPieces: [
        { type: 'pawn', color: 'black', row: 4, col: 2 }, // Screen piece horizontal
        { type: 'pawn', color: 'black', row: 4, col: 6 }, // Screen piece horizontal
        { type: 'pawn', color: 'black', row: 2, col: 4 }, // Screen piece vertical
        { type: 'pawn', color: 'black', row: 6, col: 4 }, // Screen piece vertical
        { type: 'pawn', color: 'black', row: 4, col: 1 }, // Target
        { type: 'pawn', color: 'black', row: 4, col: 7 }, // Target
        { type: 'pawn', color: 'black', row: 1, col: 4 }, // Target
        { type: 'pawn', color: 'black', row: 7, col: 4 }  // Target
      ]
    },
    {
      piece: { type: 'advisor', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Advisor',
      description: 'Advisors can move one or two squares in any direction (like a mini-queen). They originate from Xianqi (Chinese Chess) but have expanded movement in Ranga. They are versatile and powerful pieces.',
      validMoves: [
        // 1 square in all 8 directions
        { row: 5, col: 4 }, { row: 5, col: 5 }, { row: 4, col: 5 },
        { row: 3, col: 5 }, { row: 3, col: 4 }, { row: 3, col: 3 },
        { row: 4, col: 3 }, { row: 5, col: 3 },
        // 2 squares in all 8 directions
        { row: 6, col: 4 }, { row: 6, col: 6 }, { row: 4, col: 6 },
        { row: 2, col: 6 }, { row: 2, col: 4 }, { row: 2, col: 2 },
        { row: 4, col: 2 }, { row: 6, col: 2 }
      ],
      opponentPieces: []
    },
    {
      piece: { type: 'camel', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Camel',
      description: 'Camels are 3-1 leapers (like an extended knight). They move 3 squares in one direction and 1 square perpendicular, and can jump over pieces.',
      validMoves: [
        // 3 up, 1 left/right
        { row: 7, col: 3 }, { row: 7, col: 5 },
        // 3 down, 1 left/right
        { row: 1, col: 3 }, { row: 1, col: 5 },
        // 3 left, 1 up/down
        { row: 3, col: 1 }, { row: 5, col: 1 },
        // 3 right, 1 up/down
        { row: 3, col: 7 }, { row: 5, col: 7 }
      ],
      opponentPieces: []
    },
    {
      piece: { type: 'ram', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Ram (Battering Ram)',
      description: 'Rams move like Rooks in International Chess (horizontally or vertically). They can move to empty squares OR bump a piece (enemy or friendly), pushing it one square further only if the square behind it is empty. If an ENEMY piece is on the edge of the board, the Ram can bump it off to capture. Own pieces can never be bumped off the board. As with any move, a Ram cannot bump into a position that would leave its own king in check.',
      validMoves: [
        // Normal rook-like moves to empty squares (stops before enemy)
        { row: 4, col: 3 }, { row: 4, col: 5 }, { row: 3, col: 4 }, { row: 5, col: 4 },
        { row: 4, col: 2 }, { row: 2, col: 4 },
        { row: 4, col: 1 }, { row: 1, col: 4 },
        { row: 4, col: 0 }, { row: 0, col: 4 },
        // Push moves (ram moves to enemy square, pushes enemy one square further)
        { row: 4, col: 6 }, // Ram moves here, pushes black pawn to col 7
        { row: 6, col: 4 }  // Ram moves here, pushes black pawn to row 7
      ],
      opponentPieces: [
        { type: 'pawn', color: 'black', row: 4, col: 6 }, // Will be pushed to col 7
        { type: 'pawn', color: 'black', row: 6, col: 4 }  // Will be pushed to row 7
      ]
    },
    {
      piece: { type: 'spy', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Spy',
      description: 'Spies are one of the trickiest pieces in Ranga. They move 1 square in any direction OR any number of squares diagonally backward OR vertically backward. Initially they don\'t cover much territory but grow where they can attack as they move forward. When a Spy reaches the opposite end, the enemy King is frozen in its surrounding 9 squares until the Spy is killed. Each player gets one Spy.',
      validMoves: [
        // 1 square any direction
        { row: 5, col: 4 }, { row: 5, col: 5 }, { row: 4, col: 5 },
        { row: 3, col: 5 }, { row: 3, col: 4 }, { row: 3, col: 3 },
        { row: 4, col: 3 }, { row: 5, col: 3 },
        // Any number backwards vertically (toward row 0 for white)
        { row: 2, col: 4 }, { row: 1, col: 4 }, { row: 0, col: 4 },
        // Any number backwards diagonally (toward row 0)
        { row: 2, col: 2 }, { row: 1, col: 1 }, { row: 0, col: 0 },
        { row: 2, col: 6 }, { row: 1, col: 7 }, { row: 0, col: 8 }
      ],
      opponentPieces: []
    },
    {
      piece: { type: 'donkey', color: 'white' },
      position: { row: 4, col: 4 },
      title: 'Donkey',
      description: 'Donkeys are the most powerful piece in Ranga. They move 1 square in any direction OR alternating diagonal-straight patterns (e.g., up-right, up, up-right, up... OR down-left, left, down-left, left...). Donkeys cannot jump over pieces.',
      validMoves: [
        // 1 square in all 8 directions (King-like)
        { row: 5, col: 4 }, { row: 5, col: 5 }, { row: 4, col: 5 },
        { row: 3, col: 5 }, { row: 3, col: 4 }, { row: 3, col: 3 },
        { row: 4, col: 3 }, { row: 5, col: 3 },
        // Up-right diagonal, up straight pattern (f6, e7, f8...)
        { row: 5, col: 5 }, { row: 6, col: 4 }, { row: 7, col: 5 }, { row: 8, col: 4 },
        // Up-left diagonal, up straight pattern (d6, e7, d8...)
        { row: 5, col: 3 }, { row: 6, col: 4 }, { row: 7, col: 3 }, { row: 8, col: 4 },
        // Left diagonal, left straight pattern (d6, c5, b6, a5)
        { row: 5, col: 3 }, { row: 4, col: 2 }, { row: 5, col: 1 }, { row: 4, col: 0 },
        // Down-left diagonal, left straight (d4, c5, b4, a5)
        { row: 3, col: 3 }, { row: 4, col: 2 }, { row: 3, col: 1 }, { row: 4, col: 0 },
        // Down-left diagonal, down straight (d4, e3, d2, e1)
        { row: 3, col: 3 }, { row: 2, col: 4 }, { row: 1, col: 3 }, { row: 0, col: 4 },
        // Down-right diagonal, down straight (f4, e3, f2, e1)
        { row: 3, col: 5 }, { row: 2, col: 4 }, { row: 1, col: 5 }, { row: 0, col: 4 },
        // Down-right diagonal, right straight (f4, g5, h4, i5)
        { row: 3, col: 5 }, { row: 4, col: 6 }, { row: 3, col: 7 }, { row: 4, col: 8 },
        // Up-right diagonal, right straight (f6, g5, h6, i5)
        { row: 5, col: 5 }, { row: 4, col: 6 }, { row: 5, col: 7 }, { row: 4, col: 8 }
      ],
      opponentPieces: []
    }
  ];

  const currentTutorial = tutorialSteps[currentStep];

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep]);

  // Check if a square should be highlighted
  const isValidMove = (row, col) => {
    return currentTutorial.validMoves.some(
      move => move.row === row && move.col === col
    );
  };

  // Check if there's an opponent piece at this position
  const getOpponentPiece = (row, col) => {
    return currentTutorial.opponentPieces.find(
      piece => piece.row === row && piece.col === col
    );
  };

  // Render the tutorial board
  const renderBoard = () => {
    const squares = [];
    for (let row = 8; row >= 0; row--) {
      for (let col = 0; col < 9; col++) {
        const isDark = (row + col) % 2 === 1;
        const isMain = row === currentTutorial.position.row && col === currentTutorial.position.col;
        const isHighlighted = isValidMove(row, col);
        const opponentPiece = getOpponentPiece(row, col);

        let squareClass = `tutorial-square ${isDark ? 'dark' : ''}`;
        if (isHighlighted) squareClass += ' highlighted';

        squares.push(
          <div key={`${row}-${col}`} className={squareClass}>
            {isMain && (
              <div className="tutorial-piece main-piece">
                {getPieceImage(currentTutorial.piece.type, currentTutorial.piece.color)}
              </div>
            )}
            {opponentPiece && (
              <div className="tutorial-piece opponent-piece">
                {getPieceImage(opponentPiece.type, opponentPiece.color)}
              </div>
            )}
          </div>
        );
      }
    }
    return squares;
  };

  // Piece images
  const PIECE_IMAGES = {
    white: {
      king: '/assets/pieces/white-king.png',
      knight: '/assets/pieces/white-knight.png',
      pawn: '/assets/pieces/white-pawn.png',
      advisor: '/assets/pieces/white-advisor.png',
      cannon: '/assets/pieces/white-cannon.png',
      camel: '/assets/pieces/white-camel.png',
      donkey: '/assets/pieces/white-donkey.png',
      ram: '/assets/pieces/white-ram.png',
      spy: '/assets/pieces/white-spy.png'
    },
    black: {
      king: '/assets/pieces/black-king.png',
      knight: '/assets/pieces/black-knight.png',
      pawn: '/assets/pieces/black-pawn.png',
      advisor: '/assets/pieces/black-advisor.png',
      cannon: '/assets/pieces/black-cannon.png',
      camel: '/assets/pieces/black-camel.png',
      donkey: '/assets/pieces/black-donkey.png',
      ram: '/assets/pieces/black-ram.png',
      spy: '/assets/pieces/black-spy.png'
    }
  };

  const getPieceImage = (type, color) => {
    const imagePath = PIECE_IMAGES[color][type];
    return <img src={imagePath} alt={`${color} ${type}`} className="piece-image" />;
  };

  return (
    <div className="tutorial-container" 
         onTouchStart={onTouchStart}
         onTouchMove={onTouchMove}
         onTouchEnd={onTouchEnd}>
      <div className="tutorial-header">
        <button className="tutorial-back-button" onClick={onComplete}>
          ← Back to Menu
        </button>
        <h1>Learn Ranga</h1>
      </div>

      <div className="tutorial-content">
        <div className="tutorial-board-container">
          <div className="tutorial-board">
            {renderBoard()}
          </div>
        </div>

        <div className="tutorial-info">
          <h2>{currentTutorial.title}</h2>
          <p>{currentTutorial.description}</p>
          
          <div className="tutorial-progress">
            <span>Piece {currentStep + 1} of {tutorialSteps.length}</span>
            <div className="progress-dots">
              {tutorialSteps.map((_, index) => (
                <span 
                  key={index} 
                  className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="tutorial-navigation">
            <button 
              className="nav-button prev" 
              onClick={handlePrev}
              disabled={currentStep === 0}
            >
              ← Previous
            </button>
            
            {currentStep < tutorialSteps.length - 1 ? (
              <button className="nav-button next" onClick={handleNext}>
                Next →
              </button>
            ) : (
              <button className="nav-button ready" onClick={onComplete}>
                Ready to Play! 🎮
              </button>
            )}
          </div>

          <div className="swipe-hint">
            👆 Swipe or use arrow keys to navigate
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;
